import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLRouteNames } from '../../../../routing/sml/SMLRoutes';
import { PromoteContainer } from '../PromoteContainer';
import { PostsHeadActions } from '../posts/PostsHeadActions';
import { PostsTabRow } from '../posts/PostsTabRow';
import { postsMessages } from '../posts/postsMessages';
import type { CalendarPost } from '../types';
import { CalendarBody } from './CalendarBody';
import { CalendarEmptyState } from './CalendarEmptyState';
import { CalendarFilters } from './CalendarFilters';
import { CalendarLaunchDialogs } from './CalendarLaunchDialogs';
import { CalendarLaunchSummary } from './CalendarLaunchSummary';
import { CalendarLegend } from './CalendarLegend';
import { calendarMessages } from './calendarMessages';
import { CalendarToolbar } from './CalendarToolbar';
import type { PostPanelHandlers } from './dayPanelTypes';
import { DeletePostConfirm } from './DeletePostConfirm';
import { MoveUndoNote } from './MoveUndoNote';
import { NeedsYouStrip } from './NeedsYouStrip';
import { useCalendarDialogs } from './useCalendarDialogs';
import { useCalendarLaunch } from './useCalendarLaunch';
import { useCalendarScreen } from './useCalendarScreen';
import { usePeriodTitle } from './usePeriodTitle';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';

const EMPTY_POSTS: CalendarPost[] = [];

interface PromoteCalendarScreenProps {
  accountId: number;
}

/**
 * Posts → Calendar (design promote-calendar a, b, c): Month, Week and List, the filters in the
 * address, the release context, the day panel and the move of a post. The head is the Posts head
 * with the tabs Board · Calendar (design navigation-v2 c 3c); the Calendar has no title of its own.
 * The realtime events keep it fresh.
 */
const PromoteCalendarScreenComponent: React.FC<PromoteCalendarScreenProps> = ({ accountId }) => {
  const intl = useIntl();
  const screen = useCalendarScreen(accountId);
  // design: music24-demo-artist a4. The demo artist is read-only.
  const isDemo = useIsDemoAccount();
  const { filters, range, actions, move } = screen;
  const { closePanel, selectedPost } = screen;
  const onDeleted = useCallback(
    (post: CalendarPost) => (selectedPost?.uuid === post.uuid ? closePanel() : undefined),
    [closePanel, selectedPost]
  );
  const dialogs = useCalendarDialogs({ actions, onDeleted });
  const launch = useCalendarLaunch(accountId);
  const title = usePeriodTitle(filters.view, range.days, filters.anchor);
  const panelHandlers: PostPanelHandlers = useMemo(
    () => ({
      actions,
      onEditInComposer: screen.onEditInComposer,
      onOpenChannels: screen.openChannels,
      onOpenSocialPost: screen.openSocialPost,
      onScheduleDrafts: launch.scheduleDrafts,
      onAskDelete: dialogs.askDelete,
    }),
    [
      actions,
      dialogs.askDelete,
      launch.scheduleDrafts,
      screen.onEditInComposer,
      screen.openChannels,
      screen.openSocialPost,
    ]
  );
  const isEmpty = range.data !== undefined && range.data.posts.length === 0 && range.data.rolloutDays.length === 0;

  return (
    <PromoteContainer
      title={intl.formatMessage(postsMessages.title)}
      subtitle={intl.formatMessage(postsMessages.subtitle)}
      crumbs={[intl.formatMessage(postsMessages.title), intl.formatMessage(calendarMessages.title)]}
      actions={<PostsHeadActions accountId={accountId} onNewPost={screen.newPost} />}
      tabs={<PostsTabRow accountId={accountId} screen={SMLRouteNames.promoteCalendar} />}
    >
      <NeedsYouStrip posts={screen.needsYou} onShow={screen.showNeedsYou} />
      <CalendarLaunchSummary
        launch={launch}
        posts={range.data?.posts ?? EMPTY_POSTS}
        channels={screen.channels}
        isReadOnly={isDemo}
        onOpenPost={screen.onOpenPost}
        onOpenChannels={screen.openChannels}
      />
      <CalendarToolbar
        view={filters.view}
        title={title}
        onPrevious={screen.previous}
        onNext={screen.next}
        onToday={screen.today}
        onView={filters.setView}
      />
      <CalendarFilters
        posts={range.data?.posts ?? []}
        songs={range.data?.songs ?? []}
        filters={filters.filters}
        isFiltered={filters.isFiltered}
        onChange={filters.setFilter}
        onClear={filters.clearFilters}
      />
      {range.isLoading ? <LoadingView /> : null}
      {!range.isLoading && range.error !== undefined ? <ErrorView error={range.error} retry={range.retry} /> : null}
      {!range.isLoading && range.error === undefined && !screen.hasChannel && isEmpty ? (
        <CalendarEmptyState
          kind="noChannel"
          periodLabel={title}
          onOpenReleases={screen.openReleases}
          onNewPost={screen.newPost}
          onOpenChannels={screen.openChannels}
        />
      ) : null}
      {!range.isLoading && range.error === undefined && screen.hasChannel && isEmpty ? (
        <CalendarEmptyState
          kind="empty"
          periodLabel={title}
          onOpenReleases={screen.openReleases}
          onNewPost={screen.newPost}
          onOpenChannels={screen.openChannels}
        />
      ) : null}
      {!range.isLoading && range.error === undefined ? (
        <CalendarBody screen={screen} panelHandlers={panelHandlers} />
      ) : null}
      {!range.isLoading && range.error === undefined && filters.view !== 'list' ? <CalendarLegend /> : null}
      <MoveUndoNote text={move.noteText} onUndo={move.undo} onClose={move.closeNote} />
      <CalendarLaunchDialogs accountId={accountId} launch={launch} />
      <DeletePostConfirm
        post={dialogs.pendingDelete}
        isBusy={actions.isWriting}
        onConfirm={dialogs.confirmDelete}
        onCancel={dialogs.cancelDelete}
      />
    </PromoteContainer>
  );
};

export const PromoteCalendarScreen = TypedMemo(PromoteCalendarScreenComponent);
